import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ListTodo } from 'lucide-react';
import { ProfessionalDashboardSchil, WERKPLEK_SIGNALEN_KEY } from '../components/ProfessionalDashboardSchil';
import { WerkplekSignaalLijst } from '../components/WerkplekSignaalLijst';
import { werkplekApi } from '../services/professionalWerkplekApi';

/**
 * Dashboard › Actiepunten: acute, tijdgevoelige taken (urgent + actie).
 * Een actiepunt verdwijnt pas als de zaak op de doelpagina is afgehandeld.
 */
const ProfessionalWerkplekActiepuntenPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const { data, isLoading, isError } = useQuery({ queryKey: WERKPLEK_SIGNALEN_KEY, queryFn: werkplekApi.signalen });

  const actiepunten = (data?.signalen ?? []).filter((s) => s.source === 'afgeleid' && s.severity !== 'info');
  const urgent = actiepunten.filter((s) => s.severity === 'urgent');
  const overig = actiepunten.filter((s) => s.severity !== 'urgent');

  return (
    <ProfessionalDashboardSchil
      titel={t('werkplek.actiepunten.titel')}
      ondertitel={t('werkplek.actiepunten.ondertitel')}
      acties={
        <Link
          to="/professional/action-items"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-navy-800 hover:bg-navy-50"
        >
          <ListTodo className="w-4 h-4" />
          {t('werkplek.actiepunten.eigenActiepunten')}
        </Link>
      }
    >
      <div className="p-4 sm:p-6 space-y-6">
        {isLoading && <div className="h-24 rounded-lg bg-white border border-gray-200 animate-pulse" />}
        {isError && <p className="text-sm text-red-600">{t('werkplek.laadFout')}</p>}

        {!isLoading && !isError && actiepunten.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-600" />
            <p className="mt-3 font-semibold text-navy-900">{t('werkplek.actiepunten.leegTitel')}</p>
            <p className="mt-1 text-sm text-navy-500">{t('werkplek.actiepunten.leegTekst')}</p>
          </div>
        )}

        {urgent.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-red-700 mb-2">
              {t('werkplek.actiepunten.urgent', { count: urgent.length })}
            </h2>
            <WerkplekSignaalLijst signalen={urgent} />
          </section>
        )}

        {overig.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-navy-500 mb-2">
              {t('werkplek.actiepunten.handelen', { count: overig.length })}
            </h2>
            <WerkplekSignaalLijst signalen={overig} />
          </section>
        )}
      </div>
    </ProfessionalDashboardSchil>
  );
};

export default ProfessionalWerkplekActiepuntenPage;
